import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../pages/dashboard/DashboardUI.module.css';
import settingsStyles from '../../pages/dashboard/Settings.module.css';
import * as teamRoleService from '../../services/teamRoleService';
import * as permissionService from '../../services/permissionService';

export default function TeamDefaultsManager({ onMessage }) {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(new Set());
  const [formData, setFormData] = useState({
    role_name: '',
    description: '',
    permissions: [],
    is_default: false
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [rolesData, permsData] = await Promise.all([
        teamRoleService.getTeamRoles(),
        permissionService.getPermissionsByCategory()
      ]);
      
      // Handle permissionService response format
      if (permsData.success) {
        setRoles(rolesData);
        
        // Transform permissions object to array format
        // API returns: { "dashboard": [...], "leads": [...] }
        // We need: [{ category: "dashboard", permissions: [...] }, ...]
        const permissionsObj = permsData.data?.permissions || {};
        const permissionsArray = Object.keys(permissionsObj).map(category => ({
          category: category,
          permissions: permissionsObj[category] || []
        }));
        
        setAllPermissions(permissionsArray);
      } else {
        throw new Error(permsData.error || 'Failed to load permissions');
      }
    } catch (error) {
      onMessage({ type: 'error', text: error.message });
      // Set empty arrays on error to prevent crashes
      setRoles([]);
      setAllPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleCategoryCollapse = (categoryName) => {
    setCollapsedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const handleCreate = () => {
    setEditingRole(null);
    setFormData({
      role_name: '',
      description: '',
      permissions: [],
      is_default: false
    });
    setCollapsedCategories(new Set()); // Reset collapsed state
    setShowModal(true);
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      role_name: role.role_name,
      description: role.description || '',
      permissions: role.permissions || [],
      is_default: role.is_default || false
    });
    setCollapsedCategories(new Set()); // Reset collapsed state
    setShowModal(true);
  };

  const handleDelete = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) {
      return;
    }

    try {
      await teamRoleService.deleteTeamRole(roleId);
      onMessage({ type: 'success', text: 'Role deleted successfully' });
      loadData();
    } catch (error) {
      onMessage({ type: 'error', text: error.message });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingRole) {
        await teamRoleService.updateTeamRole(editingRole.role_id, formData);
        onMessage({ type: 'success', text: 'Role updated successfully' });
      } else {
        await teamRoleService.createTeamRole(formData);
        onMessage({ type: 'success', text: 'Role created successfully' });
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      onMessage({ type: 'error', text: error.message });
    }
  };

  const togglePermission = (permissionName) => {
    setFormData(prev => {
      const hasPermission = prev.permissions.includes(permissionName);
      return {
        ...prev,
        permissions: hasPermission
          ? prev.permissions.filter(p => p !== permissionName)
          : [...prev.permissions, permissionName]
      };
    });
  };

  const toggleCategoryPermissions = (category) => {
    const categoryPermissions = category.permissions.map(p => p.name);
    const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
    
    setFormData(prev => {
      if (allSelected) {
        // Remove all category permissions
        return {
          ...prev,
          permissions: prev.permissions.filter(p => !categoryPermissions.includes(p))
        };
      } else {
        // Add all category permissions
        const newPerms = [...prev.permissions];
        categoryPermissions.forEach(p => {
          if (!newPerms.includes(p)) {
            newPerms.push(p);
          }
        });
        return { ...prev, permissions: newPerms };
      }
    });
  };

  if (loading) {
    return <div className={settingsStyles.loading}>Loading team roles...</div>;
  }

  return (
    <div className={settingsStyles.section}>
      <div className={settingsStyles.sectionHeader}>
        <h3>Team Defaults / Roles</h3>
        <button className={styles.buttonPrimary} onClick={handleCreate}>
          + Add Role
        </button>
      </div>
      <p className={settingsStyles.cardDesc}>
        Define reusable permission profiles for team members.
      </p>

      <div className={settingsStyles.rolesList}>
        {roles.length === 0 ? (
          <div className={settingsStyles.emptyState}>
            <p>No roles defined yet. Create your first role to get started.</p>
          </div>
        ) : (
          roles.map(role => (
            <div key={role.role_id} className={settingsStyles.roleCard}>
              <div className={settingsStyles.roleHeader}>
                <div>
                  <h4>
                    {role.role_name}
                    {role.is_default && (
                      <span className={settingsStyles.defaultBadge}>Default</span>
                    )}
                  </h4>
                  {role.description && (
                    <p className={settingsStyles.roleDesc}>{role.description}</p>
                  )}
                </div>
                <div className={settingsStyles.roleActions}>
                  <button
                    className={styles.buttonSecondary}
                    onClick={() => handleEdit(role)}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.buttonDanger}
                    onClick={() => handleDelete(role.role_id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className={settingsStyles.rolePermissions}>
                <strong>{role.permissions?.length || 0}</strong> permissions assigned
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className={settingsStyles.modalOverlay}>
          <form className={settingsStyles.modal} onSubmit={handleSubmit}>
            <div className={settingsStyles.modalHeader}>
              <h3>{editingRole ? 'Edit Role' : 'Create Role'}</h3>
              <button
                className={settingsStyles.closeButton}
                onClick={() => setShowModal(false)}
                type="button"
              >
                ×
              </button>
            </div>
            <div className={settingsStyles.modalContent}>
              <div className={settingsStyles.formGroup}>
                <label>Role Name *</label>
                <input
                  type="text"
                  value={formData.role_name}
                  onChange={(e) => setFormData({ ...formData, role_name: e.target.value })}
                  required
                  placeholder="e.g., Project Manager, Developer, Viewer"
                />
              </div>

                <div className={settingsStyles.formGroup}>
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this role and when to use it"
                    rows="3"
                  />
                </div>

                <div className={settingsStyles.formGroup}>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    />
                    {' '}Set as default role for new team members
                  </label>
                </div>

                <div className={settingsStyles.permissionsSection}>
                  <h4>Permissions</h4>
                  <p className={settingsStyles.permissionsHint}>
                    Select the permissions to include in this role
                  </p>
                  
                  {Array.isArray(allPermissions) && allPermissions.length > 0 ? (
                    allPermissions.map(category => {
                      const categoryPermissions = category.permissions.map(p => p.name);
                      const allSelected = categoryPermissions.every(p => formData.permissions.includes(p));
                      const someSelected = categoryPermissions.some(p => formData.permissions.includes(p));
                      const isCollapsed = collapsedCategories.has(category.category);
                      
                      return (
                        <div key={category.category} className={settingsStyles.permissionCategory}>
                          <div className={settingsStyles.categoryHeader}>
                            <button
                              type="button"
                              className={settingsStyles.collapseButton}
                              onClick={() => toggleCategoryCollapse(category.category)}
                              aria-label={isCollapsed ? 'Expand category' : 'Collapse category'}
                            >
                              {isCollapsed ? '▶' : '▼'}
                            </button>
                            <label>
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={input => {
                                  if (input) {
                                    input.indeterminate = someSelected && !allSelected;
                                  }
                                }}
                                onChange={() => toggleCategoryPermissions(category)}
                              />
                              <strong>{category.category.toUpperCase()}</strong>
                            </label>
                          </div>
                          {!isCollapsed && (
                            <div className={settingsStyles.permissionList}>
                              {category.permissions.map(permission => (
                                <label key={permission.name} className={settingsStyles.permissionItem}>
                                  <input
                                    type="checkbox"
                                    checked={formData.permissions.includes(permission.name)}
                                    onChange={() => togglePermission(permission.name)}
                                  />
                                  {permission.description || permission.name}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={settingsStyles.emptyState}>
                      <p>No permissions available. Please check your permissions configuration.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className={settingsStyles.modalActions}>
                <button
                  type="button"
                  className={styles.buttonSecondary}
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className={styles.buttonPrimary}>
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
          </form>
        </div>
      )}
    </div>
  );
}
